import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';

import { createDiffs, type RawDiff } from './createDiffs';
import type { DiffPostProcessor } from './postProcessing';

export type IgnoredRange = { pos: number; size: number; type: 'mark' | 'inlineNode' };

export type DiffFragment = {
	/**
	 * This represents the starting position in the original content for which the diff applies
	 */
	from: number;

	/**
	 * This represents the ending position in the original content for which the diff applies
	 */
	to: number;

	/**
	 * This represents the type of diff operation that is to be performed, is content to be replaced, added or deleted.
	 */
	type: 'REPLACEMENT' | 'ADDITION' | 'DELETION';

	/**
	 * This is the original fragment of the content which is to be replaced at the from/to positions.
	 * This is Fragment.empty if the target is to be added.
	 */
	original: Fragment;

	/**
	 * This is the replacement fragment of the original which is to be inserted at the from/to positions.
	 * Otherwise this is Fragment.empty if the target is to be deleted.
	 */
	replacement: Fragment;

	/**
	 *	This flag will be set if the replacements only contains whitespace.
	 */
	isWhiteSpace: boolean;

	/**
	 * This flag will be set if the diff contains inline nodes.
	 */
	hasInlineNodes: boolean;
};

const normaliseType = (diff: RawDiff, nextDiff?: RawDiff) => {
	if (diff.type === -1 && nextDiff?.type === 1) {
		// Lookahead 1 diff to see if the next diff is an Addition
		return 'REPLACEMENT';
	}
	// delete
	else if (diff.type === -1) {
		return 'DELETION';
	}
	// add
	else if (diff.type === 1 && (!nextDiff || (nextDiff && nextDiff.type !== -1))) {
		return 'ADDITION';
	}
};

function isIntersectingWithIgnoredRanges(
	diff: DiffFragment,
	ignoredRanges: Array<IgnoredRange>,
	originalFrom: number,
) {
	return ignoredRanges.some(({ pos, size }) => {
		if (diff.to < originalFrom + pos || diff.from > originalFrom + pos + size) {
			return false;
		}
		return true;
	});
}

type NodeAlignment = {
	original: Fragment;
	corrected: Fragment;
	offset: number;
};

/**
 * Aligns nodes between original and corrected fragments to identify text differences.
 *
 * This function identifies text blocks in both fragments and creates a mapping between them
 * based on their textual similarity. It uses dynamic programming to find the optimal matching
 * of nodes.
 *
 * @param original - The original fragment to compare
 * @param corrected - The corrected fragment to compare against
 * @param offset - Position offset to apply to node positions
 * @returns Array of NodeAlignment objects describing the mapping between original and corrected content
 */
const alignNodes = (original: Fragment, corrected: Fragment, offset: number): NodeAlignment[] => {
	const originalNodes: [PMNode, number][] = [];
	const correctedNodes: [PMNode, number][] = [];

	original.descendants((node, pos) => {
		if (node.isTextblock) {
			originalNodes.push([node, offset + pos]);
			return false;
		}
		return true;
	});
	if (originalNodes.length === 0) {
		return correctedNodes.map(([node, pos]) => ({
			original: Fragment.empty,
			corrected: node.content,
			offset: offset + pos,
		}));
	}

	corrected.descendants((node, pos) => {
		if (node.isTextblock) {
			correctedNodes.push([node, offset + pos]);
			return false;
		}
		return true;
	});
	if (correctedNodes.length === 0) {
		return originalNodes.map(([node, pos]) => ({
			original: node.content,
			corrected: Fragment.empty,
			offset: pos,
		}));
	}

	// Calculate a similarity matrix between the nodes.
	const similarities: number[][] = [];
	for (let i = 0; i < originalNodes.length; i++) {
		similarities[i] = [];
		for (let j = 0; j < correctedNodes.length; j++) {
			similarities[i][j] = calculateSimilarity(
				originalNodes[i][0].textContent,
				correctedNodes[j][0].textContent,
			);
		}
	}

	// DP[i][j] = maximum similarity achievable for first i original nodes and first j corrected nodes.
	const dp: number[][] = Array(originalNodes.length + 1)
		.fill(0)
		.map(() => Array(correctedNodes.length + 1).fill(0));

	// To reconstruct the matching, track the decisions made.
	const decisions: ('match' | 'skip-original' | 'skip-corrected')[][] = Array(
		originalNodes.length + 1,
	)
		.fill(0)
		.map(() => Array(correctedNodes.length + 1).fill(''));

	// Fill the DP table.
	for (let i = 1; i <= originalNodes.length; i++) {
		for (let j = 1; j <= correctedNodes.length; j++) {
			const skipOriginal = dp[i - 1][j];
			const skipCorrected = dp[i][j - 1];

			// Match nodes if similarity is above threshold.
			const similarity = similarities[i - 1][j - 1];
			const match = dp[i - 1][j - 1] + similarity;

			// Choose the best option.
			if (match >= skipOriginal && match >= skipCorrected) {
				dp[i][j] = match;
				decisions[i][j] = 'match';
			} else if (skipOriginal >= skipCorrected) {
				dp[i][j] = skipOriginal;
				decisions[i][j] = 'skip-original';
			} else {
				dp[i][j] = skipCorrected;
				decisions[i][j] = 'skip-corrected';
			}
		}
	}

	const matches: [number, number][] = [];
	const matchedOriginal = new Set<number>();
	const matchedCorrected = new Set<number>();

	// Reconstruct the matches by backtracking.
	let i = originalNodes.length;
	let j = correctedNodes.length;
	while (i > 0 && j > 0) {
		if (decisions[i][j] === 'match') {
			matches.push([i - 1, j - 1]);
			matchedOriginal.add(i - 1);
			matchedCorrected.add(j - 1);
			i--;
			j--;
		} else if (decisions[i][j] === 'skip-original') {
			i--;
		} else {
			j--;
		}
	}
	matches.reverse(); // Reverse matches to maintain original order.

	const diffTasks: NodeAlignment[] = [];

	// Add matched pairs.
	for (const [i, j] of matches) {
		diffTasks.push({
			original: originalNodes[i][0].content,
			corrected: correctedNodes[j][0].content,
			offset: originalNodes[i][1],
		});
	}

	// Add unmatched original nodes as deletions.
	for (let i = 0; i < originalNodes.length; i++) {
		if (!matchedOriginal.has(i)) {
			diffTasks.push({
				original: originalNodes[i][0].content,
				corrected: Fragment.empty,
				offset: originalNodes[i][1],
			});
		}
	}

	// Add unmatched corrected nodes as additions.
	for (let j = 0; j < correctedNodes.length; j++) {
		if (!matchedCorrected.has(j)) {
			let insertPos = offset;
			// Try to place after the last matched node that comes before this one.
			for (let k = 0; k < j; k++) {
				if (matchedCorrected.has(k)) {
					const originalIndex = matches.find(([_, corrIndex]) => corrIndex === k)?.[0];
					if (originalIndex === undefined) {
						continue;
					}
					const nodeEndPos =
						originalNodes[originalIndex][1] + originalNodes[originalIndex][0].nodeSize;
					insertPos = Math.max(insertPos, nodeEndPos);
				}
			}

			diffTasks.push({
				original: Fragment.empty,
				corrected: correctedNodes[j][0].content,
				offset: insertPos,
			});
		}
	}

	// Sort by offset to ensure proper order.
	diffTasks.sort((a, b) => a.offset - b.offset);

	return diffTasks;
};

const distance = (str1: string, str2: string): number => {
	const current: number[] = Array(str1.length + 1).fill(0);
	let prev: number = 0;
	let value: number;

	for (let i = 0; i <= str2.length; i++) {
		for (let j = 0; j <= str1.length; j++) {
			if (i && j) {
				if (str1.charAt(j - 1) === str2.charAt(i - 1)) {
					value = prev;
				} else {
					value = Math.min(current[j], current[j - 1], prev) + 1;
				}
			} else {
				value = i + j;
			}

			prev = current[j];
			current[j] = value;
		}
	}

	return current[str1.length];
};

const calculateSimilarity = (textA: string, textB: string): number => {
	if (textA === textB) {
		return 1;
	}
	if (textA.length === 0 || textB.length === 0) {
		return 0;
	}

	const lDistance = distance(textA, textB);
	const maxLength = Math.max(textA.length, textB.length);

	return 1 - lDistance / maxLength;
};

/**
 * This util can be used to generate a collection of diff object fragments using fragments as the comparator objects
 * @param original
 * @param corrected
 * @param schema
 * @param offset
 * @returns
 */
export const createDiffFromFragments = ({
	original,
	corrected,
	schema,
	offset = 0,
	ignoredRanges,
	postProcessors,
}: {
	original: Fragment;
	corrected: Fragment;
	schema: Schema;
	offset?: number;
	ignoredRanges?: IgnoredRange[];
	postProcessors?: DiffPostProcessor[];
}) => {
	const diffTasks = alignNodes(original, corrected, offset);

	return diffTasks.reduce<DiffFragment[]>((result, { original, corrected, offset }) => {
		return result.concat(
			createDiffFromParagraphs({
				original,
				corrected,
				schema,
				offset,
				ignoredRanges,
				postProcessors,
			}),
		);
	}, []);
};

const createDiffFromParagraphs = ({
	original,
	corrected,
	schema,
	offset = 0,
	ignoredRanges,
	postProcessors,
}: {
	original: Fragment;
	corrected: Fragment;
	schema: Schema;
	offset?: number;
	ignoredRanges?: IgnoredRange[];
	postProcessors?: DiffPostProcessor[];
}) => {
	const result: Array<DiffFragment> = [];

	// \uFFF9 & \uFFFB  are special unicode annotation markers representing the start/end of annotated content.
	// They're used here in this context to encode/decode inline nodes in a way which allows the text
	// diff process to still occur as normal.
	const leafNodes: PMNode[] = [];
	const { mention } = schema.nodes;
	const leafNodeComparator = (leafNode: PMNode) => {
		let i = leafNodes.findIndex((node) =>
			// Special case for mentions because mentions only retain the ID in transformations at the moment
			node.type === mention ? node.attrs.id === leafNode.attrs.id : node.eq(leafNode),
		);

		if (i < 0) {
			leafNodes.push(leafNode);
			i = leafNodes.length - 1;
		}
		return `\uFFF9${i}\uFFFB`;
	};

	// It's important we push the original nodes first, as we'll do a simple left>right scan to find a matching node later on
	// This means we will prefer the original nodes over replacing them with the corrected versions.
	const orgText = original.textBetween(0, original.size, '', leafNodeComparator);
	const correctedText = corrected.textBetween(0, corrected.size, '', leafNodeComparator);

	const diffs = createDiffs(orgText, correctedText, postProcessors).map((diff) => {
		let hasInlineNodes = false;
		const fragment = diff.text.split('\uFFF9').reduce<Fragment>((frag, item) => {
			const parts = item.split('\uFFFB');
			// There should only be 1 or 2 items in the split.
			if (parts.length > 1) {
				hasInlineNodes = true;
				// Ignored via go/ees005
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const nodeIndex = parts.shift()!;
				frag = frag.addToEnd(leafNodes[parseInt(nodeIndex)]);
			}

			const remainingText = parts.join('');
			if (remainingText.length > 0) {
				return frag.addToEnd(schema.text(remainingText));
			}

			return frag;
		}, Fragment.empty);

		return {
			type: diff.type,
			text: diff.text,
			length: diff.length,
			fragment,
			hasInlineNodes,
		};
	});

	const start = offset;
	const n = diffs.length;
	for (let i = 0; i < n; i++) {
		const currentDiff = diffs[i];
		const { type, text, fragment, hasInlineNodes } = currentDiff;
		const finalType = normaliseType(diffs[i], diffs?.[i + 1]);
		const isWhiteSpace = text.trim().length === 0;

		if (finalType === 'DELETION') {
			result.push({
				from: offset,
				to: offset + fragment.size,
				type: finalType,
				original: fragment,
				replacement: Fragment.empty,
				isWhiteSpace,
				hasInlineNodes,
			});
		} else if (finalType === 'REPLACEMENT' || finalType === 'ADDITION') {
			const obj: DiffFragment = {
				from: offset,
				to: offset,
				type: finalType,
				original: Fragment.empty,
				replacement: fragment,
				hasInlineNodes,
				isWhiteSpace,
			};

			if (finalType === 'REPLACEMENT') {
				const nextDiff = diffs?.[i + 1] ?? currentDiff;
				const shouldTrimEndingSpace = text.endsWith(' ') && nextDiff.text.endsWith(' ');
				const orgTrimEndSize = shouldTrimEndingSpace ? text.length - text.trimEnd().length : 0;
				const nextTrimEndSize = shouldTrimEndingSpace
					? nextDiff.text.length - nextDiff.text.trimEnd().length
					: 0;
				const minTrimEndSize = Math.min(orgTrimEndSize, nextTrimEndSize);

				obj.to += fragment.size - minTrimEndSize;
				obj.original = shouldTrimEndingSpace
					? fragment.cut(0, fragment.size - minTrimEndSize)
					: fragment;
				obj.replacement = shouldTrimEndingSpace
					? nextDiff.fragment.cut(0, nextDiff.fragment.size - minTrimEndSize)
					: nextDiff.fragment;
				obj.isWhiteSpace = obj.isWhiteSpace || obj.replacement.size === 0;

				i++;
			}

			if (ignoredRanges) {
				if (!isIntersectingWithIgnoredRanges(obj, ignoredRanges, start)) {
					result.push(obj);
				}
			} else {
				result.push(obj);
			}
		}

		// unchanged and removed words are part of original text
		if (type === 0 || type === -1) {
			offset += fragment.size;
		}
	}

	return result;
};
